import { useTranslation } from 'react-i18next';

// Utils
import {
  format,
  differenceInDays,
  addDays,
  isAfter,
  isEqual,
} from 'date-fns';
import { fr } from 'date-fns/locale';

// Hooks
import { useAppContext } from '../../store/AppContext';
import hasPermission, { PERMISSIONS } from '../../permissions/hasPermission';
import { VIEW_DOMAIN } from '../../constants';

const dateFnsLocales = {
  fr,
};

/**
 * @type {{PENDING: "Pending", ACCEPTED: "Accepted", REFUSED: "Refused"}}
 */
export const POST_STATUS = {
  PENDING: 'Pending',
  ACCEPTED: 'Accepted',
  REFUSED: 'Refused',
};

const usePost = () => {
  const { t } = useTranslation();
  const [{ user }] = useAppContext();

  /**
   * Get the JS Date object for the given slotId and year.
   *
   * @param {Object} params
   * @param {Number} params.slotId - The slotId for which to get the date.
   * @param {string} params.year - The year for which to get the date.
   * @returns {Date} - JS Date
   */
  const getDateFromSlotsId = ({ slotId, year }) => {
    const isSlotIdEven = slotId % 2 === 0;
    // La premier jour de l'année est le premier jour de référence pur le premier slotId
    const firstDayOfYear = new Date(+year, 0, 1);
    // Calculer le nombre de jours entre la date de référence des slotIds et la date a transformer
    const daysSinceFirstWeekFirstDay = Math.floor((slotId - 1) / 2);
    // Ajouter la différence a la date référence du début de calendrier
    const startOfYearDate = addDays(firstDayOfYear, daysSinceFirstWeekFirstDay);
    startOfYearDate.setHours(isSlotIdEven ? 13 : 9);

    return startOfYearDate;
  };
  /**
   * Get the starting slotId and ending slotId from a given JS Date
   *
   * @param {Date} jsDateToTransform - A JS Date.
   * @returns {({startSlotId:Number, endSlotId:Number, year:Number})}
   */
  const getSlotsIdFromDate = (jsDateToTransform) => {
    const yearToApply = jsDateToTransform.getFullYear();
    // récupérer la date du premier jour de l'année pour avoir les slotId 1 et 2
    const firstDayOfYear = new Date(yearToApply, 0, 1);

    // Calculer le nombre de jours entre la date de référence des slotIds et la date a transformer
    const daysDifferenceBetweenDates = differenceInDays(jsDateToTransform, firstDayOfYear);

    // Multiplier par 2 (un jour à 2 slotIds) pour avoir le slotId
    const startSlotId = daysDifferenceBetweenDates * 2 + 1;
    const endSlotId = startSlotId + 1;

    return { startSlotId, endSlotId, year: yearToApply };
  };

  /**
   * Get the starting slotId and ending slotId from a given Month ID and Year.
   *
   * @param {Object} params
   * @param {Number} params.month - Month ID.
   * @param {string} params.year - The year for which to get the date.
   * @returns {({startSlotId:Number, endSlotId:Number})}
   */
  const getSlotsIdFromMonth = ({ month, year }) => {
    const firstDayOfMonthDate = new Date(+year, month, 1);
    const lastDayOfMonthDate = new Date(+year, month + 1, 0);
    const firstDayOfMonthSlots = getSlotsIdFromDate(firstDayOfMonthDate);
    const lastDayOfMonthSlots = getSlotsIdFromDate(lastDayOfMonthDate);
    return {
      startSlotId: firstDayOfMonthSlots.startSlotId,
      endSlotId: lastDayOfMonthSlots.endSlotId,
    };
  };

  /**
   * Retrieves styling information for a post status tag based on the post's status.
   *
   * @param {String} status
   * @returns {{color: string, key: string}}
   */
  const getPostStatusTagStyling = (status) => {
    switch (status) {
      case POST_STATUS.PENDING:
        return {
          color: 'error',
          key: `post.status.${POST_STATUS.PENDING}`,
        };
      case POST_STATUS.ACCEPTED:
        return {
          color: 'success',
          key: `post.status.${POST_STATUS.ACCEPTED}`,
        };
      case POST_STATUS.REFUSED:
        return {
          color: 'error',
          key: `post.status.${POST_STATUS.REFUSED}`,
        };
      default:
        throw Error(`status: ${status} given to getPostStatusTagStyling is not a valid one`);
    }
  };
  /**
   * Retrieves a list of post statuses with their corresponding keys.
   * @returns {Array<{ value:string, key:string }>} An array of objects.
  */
  const getPostStatusList = () => Object.values(POST_STATUS).map((value) => ({
    value,
    key: `post.status.${value}`,
  }));
  /**
   * Formats and displays a date (lun. 12/12/2023) based on a given slot ID and year.
   *
   * @param {Object} params
   * @param {Number} params.slotId
   * @param {string} params.year
   * @param {String} params.locale
   * @returns {String} - Date to string like 'mon. 01/01/2024'
   */
  const displayDateFromSlotId = ({ slotId, year, locale }) => {
    const date = getDateFromSlotsId({ slotId, year });
    const dateFormat = 'EEE dd/MM/yy';

    return format(date, dateFormat, { locale: dateFnsLocales[locale] });
  };
  /**
   * Calculates the number of days between two slot IDs
   *
   * @param {Object} params
   * @param {Number} params.startSlotId
   * @param {Number} params.endSlotId
   * @returns {Number}
   */
  const computeDaysBetweenTwoSlotId = ({ startSlotId, endSlotId }) => (
    ((endSlotId - startSlotId) / 2)
  );

  /**
 * Checks if two periods overlap.
 *
 * @param {Object} period1 - The first period.
 * @param {number} period1.includedStartSlotId - The inclusive start slot ID of the first period.
 * @param {number} period1.excludedEndSlotId - The exclusive end slot ID of the first period.
 * @param {Object} period2 - The second period.
 * @param {number} period2.includedStartSlotId - The inclusive start slot ID of the second period.
 * @param {number} period2.excludedEndSlotId - The exclusive end slot ID of the second period.
 * @returns {boolean} Returns true if the periods overlap, false otherwise.
 */
  function doPeriodsOverlap(period1, period2) {
    const isOverlapping = period1.includedStartSlotId <= period2.excludedEndSlotId - 1
  && period2.includedStartSlotId <= period1.excludedEndSlotId - 1;
    return isOverlapping;
  }

  /**
   * Determines the time of day (morning or afternoon) based on a slot ID.
   *
   * @param {number} slotId
   * @returns {"morning" | "afternoon"}
   */
  const getDayTimeFromSlotId = (slotId) => (slotId % 2 === 0 ? 'afternoon' : 'morning');

  /**
   * Handle the possibility of a period straddling two different years
   *
   * @param {object} data
   * @returns {Array<object>}
   */
  const handleTwoYearsStraddleForOnePeriod = (data) => {
    const startDateYear = getSlotsIdFromDate(data.startDate).year;
    const endDateYear = getSlotsIdFromDate(data.endDate).year;

    // one year involved
    if (startDateYear === endDateYear) return [data];

    return [
      // first request from selected start date to last day of the year
      {
        ...data,
        endDate: new Date(startDateYear, 11, 31),
        endDateSlot: 'endSlotId',
      },
      // second request from first day of the year to selected end date
      {
        ...data,
        startDate: new Date(endDateYear, 0, 1),
        startDateSlot: 'startSlotId',
      },
    ];
  };

  /**
   * Get transgressed rules codes from API response
   *
   * @param {object} response
   * @returns {array}
   */
  const getTransgressedRulesCodesFromResponse = (response) => {
    if (!response?.data?.description) return [];
    const transgressedRule = response.data.description.replace('Transgressed rules: ', '');
    const errors = transgressedRule.split(',');
    return errors;
  };

  /**
   * Handle error messages from API
   *
   * @param {object} error
   * @returns {object}
   */
  const handleAPIErrorMessage = (error) => {
    // @ts-ignore - error is AxiosError, does have .response
    const errorCode = error.response.data.code;

    if (error?.response?.status === 403) return t('api.error.permissionDenied');

    switch (errorCode) {
      case 'non_respect_of_rules': {
        const errors = getTransgressedRulesCodesFromResponse(error.response);
        if (!errors?.length) return t('api.error.default_new_post');

        return (
          <div>
            {errors.map((err) => (
              <p>
                {t(
                  `api.error.${err.split(':')[0]}`,
                  {
                    count: err.split(':')[1] || '',
                  },
                )}
              </p>
            ))}
          </div>
        );
      }

      case 'leave_post_overlap': {
        return t('api.error.leave_post_overlap');
      }
      case 'resource_cannot_post_compensatory_leave': {
        return t('api.error.resource_cannot_post_compensatory_leave');
      }

      default: return t('api.error.default_new_post');
    }
  };

  /**
   * Get the times of day options
   *
   * @type {Array<{value: string, label: string}>}
   */
  const timesOfDayOptions = [
    { value: 'startSlotId', label: t('remoteWorkingAndLeave.newLeaveForm.date.morning') },
    { value: 'endSlotId', label: t('remoteWorkingAndLeave.newLeaveForm.date.afternoon') },
  ];

  /**
   * Check if connected user has the right permission to delete a post
   *
   * @param {Object} params
   * @param {string} params.year
   * @param {Object} params.post
   * @returns {boolean}
   */
  const hasDeletePostPermission = ({ year, post }) => {
    if (!user || !post?.includedStartSlotId) return false;

    // DELETE_ANY_POST or DELETE_OWN_POST
    const canDeleteAnyPost = hasPermission(PERMISSIONS.DELETE_ANY_POST, user.prm);
    const canDeleteOwnPost = hasPermission(PERMISSIONS.DELETE_OWN_POST, user.prm);
    const isPostForConnectedUser = post?.resourceId === user.id;

    // Only admins (DELETE_ANY_POST) can delete an already validated leave
    const isAcceptedLeave = post.type === VIEW_DOMAIN.LEAVE
      && post.status === POST_STATUS.ACCEPTED;
    if (isAcceptedLeave && !canDeleteAnyPost) {
      return false;
    }

    const canDelete = canDeleteAnyPost || (canDeleteOwnPost && isPostForConnectedUser);

    // DELETE_PAST_POST
    const today = new Date();
    const startDate = getDateFromSlotsId({ slotId: post.includedStartSlotId, year });
    const startDateIsPast = isEqual(today, startDate) || isAfter(today, startDate);
    const canDeletePastPost = hasPermission(PERMISSIONS.DELETE_PAST_POST, user.prm);

    return startDateIsPast ? canDelete && canDeletePastPost : canDelete;
  };

  return {
    getDateFromSlotsId,
    getSlotsIdFromDate,
    getSlotsIdFromMonth,
    getPostStatusTagStyling,
    getPostStatusList,
    displayDateFromSlotId,
    computeDaysBetweenTwoSlotId,
    doPeriodsOverlap,
    getDayTimeFromSlotId,
    handleTwoYearsStraddleForOnePeriod,
    getTransgressedRulesCodesFromResponse,
    handleAPIErrorMessage,
    timesOfDayOptions,
    hasDeletePostPermission,
  };
};

export default usePost;
